import { useNavigate } from 'react-router-dom'
import {
    Abono,
    Afastamento,
    Ajuste,
    abonos,
    afastamento,
    ajustes,
    registros,
} from 'service/api'
import { toast } from 'sonner'

import { StatusAprovacao } from '@acme/core'
import dayjs from 'dayjs'
import { useAuth } from 'hooks'
import {
    invalidateDigitacaoQueries,
    useUpdateDigitacaoQueryData,
} from './pages/useDigitacaoData'

export default function useService() {
    const { mutate: aprovar } = ajustes.aprovacao.mutations.useUpdate()
    const { mutate: reprovar } = ajustes.reprovacao.mutations.useUpdate()
    const { mutate: excluirAfastamento } = afastamento.mutations.useSoftDelete()
    const { mutate: excluirAbono } = abonos.mutations.useSoftDelete()
    const { mutate: excluirRegistro } = registros.mutations.useSoftDelete()
    const { mutate: atualizarAbono } = abonos.mutations.useUpdate()
    const { mutate: atualizarAfastamento } = afastamento.mutations.useUpdate()

    const { setQueryData } = useUpdateDigitacaoQueryData()
    const navigate = useNavigate()
    const {
        decoded: { categoria },
    } = useAuth()

    const onError = (e: any) => {
        toast.error(e.message || e)
    }

    const emptyPromise = () => Promise.resolve()

    const handleAprovar = async (tipo: Ajuste['tipoAjuste'], data: any) => {
        if (!data?.id) return emptyPromise()
        aprovar(
            {
                id: data.id,
                tipo,
            },
            {
                onSuccess: payload => {
                    setQueryData(
                        tipo.toUpperCase() as any,
                        { ...data, ...payload } as any,
                        !!payload.deletedAt,
                    )
                    toast.success(`${capitalize(tipo)} aprovado com sucesso`)
                    navigate(-1)
                },
                onError,
            },
        )
    }

    const handleReprovar = async (tipo: Ajuste['tipoAjuste'], data: any) => {
        if (!data?.id) return emptyPromise()
        reprovar(
            {
                ...data,
                id: data.id,
                tipo,
            },
            {
                onSuccess: async () => {
                    await invalidateDigitacaoQueries()
                    toast.success(`${capitalize(tipo)} reprovado com sucesso`)
                    navigate(-1)
                },
                onError,
            },
        )
    }

    const handleAtualizarAbono = async (data: Abono) => {
        if (!data?.id) return emptyPromise()
        atualizarAbono(
            { ...data, data: dayjs(data.data).toDate() },
            {
                onSuccess: res => {
                    setQueryData('ABONO', res)
                    toast.success('Abono atualizado com sucesso')
                    navigate(-1)
                },
                onError,
            },
        )
    }

    const handleAtualizarAfastamento = async (data: Afastamento) => {
        if (!data?.id) return emptyPromise()
        atualizarAfastamento(
            {
                ...data,
                dataInicial: dayjs.utc(data.dataInicial).toDate(),
                dataFinal: dayjs.utc(data.dataFinal).toDate(),
            },
            {
                onSuccess: res => {
                    setQueryData('AFASTAMENTO', data, true)
                    toast.success('Afastamento atualizado com sucesso')
                    setQueryData('AFASTAMENTO', res)
                    navigate(-1)
                },
                onError,
            },
        )
    }

    const handleExcluir = async (tipo: Ajuste['tipoAjuste'], data: any) => {
        if (!data?.id) return emptyPromise()
        const options = {
            onSuccess: async () => {
                setQueryData(
                    tipo.toUpperCase() as any,
                    {
                        ...data,
                        statusAprovacao:
                            categoria == 5
                                ? StatusAprovacao.PENDENTE_EXCLUSAO
                                : data.statusAprovacao,
                    },
                    categoria != 5 ||
                        (categoria == 5 &&
                            data.statusAprovacao != StatusAprovacao.APROVADO),
                )
                toast.success(`${capitalize(tipo)} excluído com sucesso`)
                navigate(-1)
            },
            onError,
        }

        switch (tipo.toUpperCase()) {
            case 'ABONO':
                excluirAbono(data, options)
                break
            case 'AFASTAMENTO':
                excluirAfastamento(data, options)
                break
            case 'REGISTRO':
                excluirRegistro(data, options)
                break
        }
    }

    function capitalize(str: string) {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    }

    return {
        handleAprovar,
        handleReprovar,
        handleExcluir,
        handleAtualizarAbono,
        handleAtualizarAfastamento,
    }
}
